import { useCallback, useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Stack } from 'expo-router';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { generateAPIUrl } from '@/utils';

type Status = 'idle' | 'submitting' | 'sent' | 'error';

/**
 * Public account-deletion request page.
 *
 * This is the external URL pasted into Google Play Console → App content →
 * Account deletion. It exists for people who have uninstalled the app and no
 * longer have a session: they enter the email tied to their account and we
 * email them a one-time confirmation link.
 *
 * No deletion happens here — POST /api/account/delete-public only sends the
 * confirmation email (and only ever to the registered address). The actual
 * delete is finalized on /confirm-delete after the user clicks that link.
 */
export default function DeleteAccountScreen() {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    const trimmed = email.trim();
    if (!trimmed) {
      setStatus('error');
      setMessage('Please enter the email address tied to your account.');
      return;
    }

    setStatus('submitting');
    setMessage(null);

    try {
      const response = await fetch(generateAPIUrl('/api/account/delete-public'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });

      const payload = (await response.json().catch(() => ({}))) as {
        message?: string;
        error?: string;
      };

      if (!response.ok) {
        setStatus('error');
        setMessage(payload.error ?? 'Something went wrong. Please try again.');
        return;
      }

      setStatus('sent');
      setMessage(
        payload.message ??
          'If an account exists for that email, a confirmation link has been sent to it.',
      );
    } catch (err) {
      setStatus('error');
      setMessage(err instanceof Error ? err.message : 'Network error');
    }
  }, [email]);

  const renderBody = () => {
    if (status === 'sent') {
      return (
        <>
          <Text style={[styles.title, { color: palette.text }]}>Check your email</Text>
          <Text style={[styles.body, { color: palette.textMuted }]}>{message}</Text>
          <Text style={[styles.body, { color: palette.textMuted }]}>
            The link expires in 30 minutes. Clicking it permanently deletes your
            FimFam account and the data we&apos;ve stored about you. Your
            calendar events stay in your device&apos;s native calendar — FimFam
            only ever reads them and never owns them.
          </Text>
        </>
      );
    }

    return (
      <>
        <Text style={[styles.title, { color: palette.text }]}>Delete your FimFam account</Text>
        <Text style={[styles.body, { color: palette.textMuted }]}>
          Enter the email address tied to your account. We&apos;ll send a
          confirmation link to that address. Deletion is only final after you
          click the link.
        </Text>
        <Text style={[styles.body, { color: palette.textMuted }]}>
          This permanently removes your profile, tasks, AI summaries, and family
          notes. Your calendar events are{' '}
          <Text style={{ fontWeight: '600' }}>not</Text> deleted — they remain in
          your native calendar (Google Calendar, iCloud, etc.).
        </Text>

        <TextInput
          value={email}
          onChangeText={setEmail}
          placeholder="you@example.com"
          placeholderTextColor={palette.textMuted}
          autoCapitalize="none"
          autoCorrect={false}
          keyboardType="email-address"
          inputMode="email"
          editable={status !== 'submitting'}
          style={[
            styles.input,
            {
              color: palette.text,
              borderColor: palette.textMuted,
              backgroundColor: palette.background,
            },
          ]}
        />

        {status === 'error' && message && (
          <Text style={[styles.error, { color: palette.danger }]}>{message}</Text>
        )}

        <TouchableOpacity
          accessibilityRole="button"
          onPress={handleSubmit}
          disabled={status === 'submitting'}
          style={[
            styles.button,
            {
              backgroundColor: palette.danger,
              opacity: status === 'submitting' ? 0.6 : 1,
            },
          ]}
        >
          {status === 'submitting' ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <Text style={styles.buttonText}>Send deletion link</Text>
          )}
        </TouchableOpacity>
      </>
    );
  };

  return (
    <View style={[styles.container, { backgroundColor: palette.background }]}>
      <Stack.Screen options={{ title: 'Delete account', headerShown: false }} />
      <View style={[styles.card, { backgroundColor: palette.cardBackground }]}>
        {renderBody()}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: Spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    padding: Spacing.xl,
    borderRadius: BorderRadius.lg,
    gap: Spacing.md,
  },
  title: { fontSize: 20, fontWeight: '600' },
  body: { fontSize: 15, lineHeight: 22 },
  input: {
    borderWidth: 1,
    borderRadius: BorderRadius.md,
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.sm,
    fontSize: 16,
  },
  error: { fontSize: 14 },
  button: {
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.md,
    alignItems: 'center',
    marginTop: Spacing.sm,
  },
  buttonText: { color: '#fff', fontSize: 16, fontWeight: '600' },
});
